'use client';

import { ChevronDown } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface OptionItem {
  value: string;
  label: string;
  description?: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (_value: string) => void;
  options: OptionItem[] | string[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string | boolean;
  required?: boolean;
  onBlur?: () => void;
  className?: string;
  icon?: React.ReactNode;
  /** When provided, search is delegated externally (no local filtering). */
  onSearchChange?: (term: string) => void;
  /** Called when the user scrolls near the bottom of the list. */
  onLoadMore?: () => void;
  /** Whether more items can be loaded. */
  hasMore?: boolean;
  /** Shows a loading indicator at the bottom of the list. */
  isLoadingMore?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  label,
  placeholder = 'Selecione uma opção',
  disabled = false,
  error = false,
  onBlur,
  className = '',
  icon,
  onSearchChange,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
}) => {
  const normalizedOptions: OptionItem[] = useMemo(() => {
    if (Array.isArray(options) && options.length > 0) {
      if (typeof (options as unknown[])[0] === 'string') {
        return (options as string[]).map((opt) => ({ value: opt, label: opt }));
      }
      return options as OptionItem[];
    }
    return [];
  }, [options]);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const isRemoteSearch = !!onSearchChange;

  const filteredOptions = useMemo(() => {
    if (isRemoteSearch || searchTerm.trim() === '') {
      return normalizedOptions;
    }
    return normalizedOptions.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, normalizedOptions, isRemoteSearch]);

  const selectedOption = useMemo(
    () => normalizedOptions.find((opt) => opt.value === value) || null,
    [normalizedOptions, value],
  );

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (onSearchChange) {
      onSearchChange(term);
    }
  };

  const loadMoreCalledRef = useRef(false);

  useEffect(() => {
    if (!isLoadingMore) loadMoreCalledRef.current = false;
  }, [isLoadingMore]);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || !onLoadMore || !hasMore || isLoadingMore || loadMoreCalledRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 60) {
      loadMoreCalledRef.current = true;
      onLoadMore();
    }
  }, [onLoadMore, hasMore, isLoadingMore]);

  useEffect(() => {
    const el = listRef.current;
    if (!el || !isOpen) return;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isOpen, handleScroll]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        if (isRemoteSearch) onSearchChange?.('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isRemoteSearch, onSearchChange]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const displayLabel = selectedOption?.label || placeholder;

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
        if (isRemoteSearch) onSearchChange?.('');
      }
      if (isOpen && onBlur) onBlur();
    }
  };

  const handleOptionSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
    if (isRemoteSearch) onSearchChange?.('');
    if (onBlur) onBlur();
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {label && (
        <div className="mb-1.5 flex items-center gap-1">
          <span className="text-caption-bold font-caption-bold text-default-font">{label}</span>
        </div>
      )}
      <div
        onClick={handleToggle}
        className={`flex w-full cursor-pointer items-center justify-between rounded-[8px] border border-solid px-3 py-2 shadow-sm transition-all duration-200 ${
          Boolean(error)
            ? 'border-error-600 ring-error-600/10 ring-4'
            : 'focus-within:border-brand-600 focus-within:ring-brand-600/10 border-neutral-300 focus-within:ring-4'
        } ${disabled ? 'cursor-not-allowed border-neutral-200 bg-neutral-50' : 'bg-white hover:border-neutral-400'}`}
      >
        <div className="flex flex-grow items-center gap-2 overflow-hidden">
          {icon && <div className="text-subtext-color flex-none">{icon}</div>}
          <span
            className={`text-body font-body truncate ${value ? 'text-default-font' : 'text-neutral-400'}`}
          >
            {displayLabel}
          </span>
        </div>
        <ChevronDown
          className={`text-subtext-color h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
        />
      </div>

      {isOpen && (
        <div className="animate-in fade-in zoom-in-95 absolute top-full right-0 left-0 z-[1000] mt-1 max-h-[400px] overflow-hidden rounded-[12px] border border-solid border-neutral-200 bg-white shadow-lg duration-200">
          <div className="border-b border-solid border-neutral-100 p-2">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchInput}
              placeholder="Buscar..."
              className="text-body font-body focus:border-brand-600 text-default-font w-full rounded-[6px] border border-solid border-neutral-200 bg-white px-3 py-1.5 shadow-sm transition-all outline-none"
            />
          </div>

          <div ref={listRef} className="max-h-[340px] overflow-y-auto p-1">
            {filteredOptions.length === 0 && !isLoadingMore ? (
              <div className="text-caption font-caption text-subtext-color p-3 text-center">
                Nenhuma opção encontrada
              </div>
            ) : (
              <>
                {filteredOptions.map((option, index) => (
                  <div
                    key={option.value || index}
                    onClick={() => handleOptionSelect(option.value)}
                    className={`text-body font-body text-default-font cursor-pointer rounded-[6px] px-3 py-2 transition-colors hover:bg-neutral-100 ${
                      option.value === value ? 'bg-brand-50 text-brand-700 font-medium' : ''
                    }`}
                  >
                    {option.label}
                  </div>
                ))}
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-3">
                    <div className="border-t-brand-600 h-4 w-4 animate-spin rounded-full border-2 border-neutral-200" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {typeof error === 'string' && error && (
        <p className="text-caption font-caption text-error-600 mt-2">{error}</p>
      )}
    </div>
  );
};

export default SearchableSelect;
